import { assertInInjectionContext, inject, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Params } from "@angular/router";

import { map } from "rxjs";

/**
 * Injects route parameters from the `ActivatedRoute` service as a reactive `Signal`.
 *
 * Overloads:
 * - `injectParams()`: Returns a `Signal` containing all route parameters as an object.
 *   route parameter by key, or `null` if the key does not exist.
 *
 * @param {string} [key] - Optional key to retrieve a specific route parameter.
 * @returns {Signal<Params | string | null>} A reactive `Signal` containing either all route parameters,
 * a specific parameter value, or `null` if the key does not exist.
 *
 * @throws Error if called outside of an Angular injection context.
 */
 
export function injectParams(): Signal<Params>;
// eslint-disable-next-line no-redeclare
export function injectParams(key: string): Signal<string | null>;
// eslint-disable-next-line no-redeclare
export function injectParams(key?: string): Signal<Params | string | null> {
  assertInInjectionContext(injectParams);
  const route = inject(ActivatedRoute);

  const params$ = route.params.pipe(map((params: Params): Params | string | null => (key ? (params?.[key] ?? null) : params)));

  return toSignal(params$, { requireSync: true });
}

/**
 * Injects query parameters from the `ActivatedRoute` service as a reactive `Signal`.
 *
 * Overloads:
 * - `injectQueryParams()`: Returns a `Signal` containing all query parameters as an object.
 * - `injectQueryParams(key: string)`: Returns a `Signal` containing the value of a specific query
 *   route parameter by key, or `null` if the key does not exist.
 *
 * @param {string} [key] - Optional key to retrieve a specific query parameter.
 * @returns {Signal<Params | string | null>} A reactive `Signal` containing either all query parameters,
 * a specific parameter value, or `null` if the key does not exist.
 *
 * @throws Error if called outside of an Angular injection context.
 */
 
export function injectQueryParams(): Signal<Params>;
// eslint-disable-next-line no-redeclare
export function injectQueryParams(key: string): Signal<string | null>;
// eslint-disable-next-line no-redeclare
export function injectQueryParams(key?: string): Signal<Params | string | null> {
  assertInInjectionContext(injectQueryParams);
  const route = inject(ActivatedRoute);

  const params$ = route.queryParams.pipe(
    map((queryParams: Params): Params | string | null => (key ? (queryParams?.[key] ?? null) : queryParams))
  );

  return toSignal(params$, { requireSync: true });
}
